import colormap from "colormap";

import { IColorPalette } from "components/user-settings/models";

/**
 * Generate an array of hex colors for the given palette name and count.
 * Checks custom palettes first so user-defined palettes take priority
 * over built-in colormap names (e.g. "cool" exists in both).
 * Custom palettes are resampled via nearest-neighbor sampling.
 * Falls back to the colormap package for standard names.
 */
export function generatePaletteColors(
  paletteName: string,
  numColors: number,
  colorPalettes: IColorPalette[] | undefined
): string[] {
  if (numColors < 1) {
    throw new Error(`numColors must be at least 1, got ${numColors}.`);
  }

  // 1. Check custom palettes first
  const palette = colorPalettes?.find(
    (p: IColorPalette) => p.name.toLowerCase() === paletteName.toLowerCase()
  );
  if (palette?.colors?.length) {
    const src = palette.colors;
    if (numColors === 1) return [src[0]];
    const result: string[] = [];
    for (let i = 0; i < numColors; i++) {
      const idx = (i / (numColors - 1)) * (src.length - 1);
      result.push(src[Math.round(idx)]);
    }
    return result;
  }

  // 2. Fall back to colormap package (works for standard names)
  try {
    return colormap({
      colormap: paletteName,
      nshades: numColors,
      format: "hex",
      alpha: 1
    });
  } catch (err) {
    // Re-throw nshades errors so the caller can display a user-friendly message
    if (err instanceof Error && err.message.includes("nshades")) {
      throw err;
    }
  }

  throw new Error(`Colour palette "${paletteName}" not found.`);
}
